import { useState } from 'react'
import {
  Alert,
  App as AntdApp,
  Button,
  Descriptions,
  Drawer,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
} from 'antd'
import { EyeOutlined, PlusOutlined, StopOutlined } from '@ant-design/icons'
import {
  useCreateReceptionShare,
  useReceptionShareBatch,
  useReceptionShareBatches,
  useReceptionShareCandidates,
  useReceptionShareableGroups,
  useRevokeReceptionShare,
} from '@/modules/receptionShare/queries'
import { formatMessageTime } from '@/utils/datetime'
import type {
  ReceptionShareBatch,
  ReceptionShareCreatePayload,
  ReceptionShareSkippedGroup,
} from '@/types/receptionShare'

const { Paragraph, Text, Title } = Typography

const STATUS_META = {
  active: { label: '代管中', color: 'processing' },
  partially_revoked: { label: '部分已收回', color: 'warning' },
  revoked: { label: '已收回', color: 'default' },
} as const

function statusTag(status: ReceptionShareBatch['status']) {
  const meta = STATUS_META[status]
  return <Tag color={meta.color}>{meta.label}</Tag>
}

function userLabel(user: { account: string; display_name: string }) {
  return user.display_name === user.account ? user.account : `${user.display_name}（${user.account}）`
}

interface DetailGroupRow {
  key: string
  room_id: string
  room_name: string
  customer_name: string
  project_name: string
  outcome: 'shared' | 'skipped'
  status?: 'active' | 'revoked'
  reason?: string | null
}

function skippedReasonLabel(reason?: string | null) {
  if (reason === 'already_writable') return '创建时已有基础可写权限'
  if (reason === 'already_shared') return '创建时已由其他批次代管'
  return '创建时已有管理权限'
}

function skippedGroupRow(group: ReceptionShareSkippedGroup): DetailGroupRow {
  return {
    key: `skipped-${group.room_id}`,
    room_id: group.room_id,
    room_name: group.current_room_name || group.room_name_snapshot,
    customer_name: group.customer_name_snapshot,
    project_name: group.project_name_snapshot,
    outcome: 'skipped',
    reason: group.reason,
  }
}

function ReceptionShareBatchDetail({ batch }: { batch: ReceptionShareBatch }) {
  const sharedRows: DetailGroupRow[] = (batch.groups || []).map((group) => ({
    key: `shared-${group.id}`,
    room_id: group.room_id,
    room_name: group.current_room_name || group.room_name_snapshot,
    customer_name: group.customer_name_snapshot,
    project_name: group.project_name_snapshot,
    outcome: 'shared',
    status: group.status,
    reason: group.revoke_reason,
  }))
  const skippedRows = (batch.creation_result?.skipped_groups || []).map(skippedGroupRow)
  const creationResult = batch.creation_result || {
    requested_count: batch.total_count,
    shared_count: batch.total_count,
    skipped_count: 0,
    skipped_groups: [],
  }
  const allRows = [...sharedRows, ...skippedRows]
  const columns = [
    {
      title: '群',
      render: (_: unknown, row: DetailGroupRow) => (
        <Space orientation="vertical" size={0}>
          <Text>{row.room_name}</Text>
          <Text type="secondary">{row.room_id}</Text>
        </Space>
      ),
    },
    { title: '客户/项目', render: (_: unknown, row: DetailGroupRow) => `${row.customer_name || '-'} / ${row.project_name || '-'}` },
    {
      title: '创建处理结果',
      width: 150,
      render: (_: unknown, row: DetailGroupRow) => row.outcome === 'shared'
        ? <Tag color="blue">新增代管</Tag>
        : <Tag color="gold">已有权限，未重复授权</Tag>,
    },
    {
      title: '当前批次状态',
      width: 130,
      render: (_: unknown, row: DetailGroupRow) => {
        if (row.outcome === 'skipped') return <Tag>不属于本批次</Tag>
        return <Tag color={row.status === 'active' ? 'processing' : 'default'}>{row.status === 'active' ? '代管中' : '已收回'}</Tag>
      },
    },
    {
      title: '说明',
      render: (_: unknown, row: DetailGroupRow) => row.outcome === 'skipped'
        ? skippedReasonLabel(row.reason)
        : (row.reason || '-'),
    },
  ]

  return (
    <Space orientation="vertical" size={16} style={{ width: '100%' }}>
      <Descriptions bordered size="small" column={1}>
        <Descriptions.Item label="批次号">{batch.batch_no}</Descriptions.Item>
        <Descriptions.Item label="状态">{statusTag(batch.status)}</Descriptions.Item>
        <Descriptions.Item label="原接待员">{userLabel(batch.source_user)}（权限保持不变）</Descriptions.Item>
        <Descriptions.Item label="代管人">{userLabel(batch.target_user)}</Descriptions.Item>
        <Descriptions.Item label="共享原因">{batch.reason || '-'}</Descriptions.Item>
        <Descriptions.Item label="创建人/时间">{batch.created_by} · {formatMessageTime(batch.created_at)}</Descriptions.Item>
        {batch.revoke_reason && <Descriptions.Item label="收回原因">{batch.revoke_reason}</Descriptions.Item>}
      </Descriptions>

      <Descriptions
        data-testid="reception-share-creation-summary"
        title="创建结果"
        bordered
        size="small"
        column={{ xs: 2, sm: 4 }}
      >
        <Descriptions.Item label="本次选择"><Text strong>{creationResult.requested_count} 个</Text></Descriptions.Item>
        <Descriptions.Item label="新增代管"><Text strong>{creationResult.shared_count} 个</Text></Descriptions.Item>
        <Descriptions.Item label="已有权限跳过"><Text strong>{creationResult.skipped_count} 个</Text></Descriptions.Item>
        <Descriptions.Item label="当前代管中"><Text strong>{batch.active_count} 个</Text></Descriptions.Item>
      </Descriptions>

      {skippedRows.length > 0 && (
        <Alert
          showIcon
          type="info"
          title={`本次选择 ${creationResult.requested_count} 个群，新增代管 ${creationResult.shared_count} 个，已有权限跳过 ${creationResult.skipped_count} 个。`}
          description="被跳过的群在创建时已可由代管人管理，因此系统没有重复授权；收回本批次时不会移除这些原有权限。"
        />
      )}

      <Tabs
        items={[
          { key: 'all', label: `全部（${allRows.length}）`, children: <Table rowKey="key" size="small" pagination={false} dataSource={allRows} columns={columns} /> },
          { key: 'shared', label: `本批次新增（${sharedRows.length}）`, children: <Table rowKey="key" size="small" pagination={false} dataSource={sharedRows} columns={columns} /> },
          { key: 'skipped', label: `已有权限跳过（${skippedRows.length}）`, children: <Table rowKey="key" size="small" pagination={false} dataSource={skippedRows} columns={columns} /> },
        ]}
      />
    </Space>
  )
}

export default function ReceptionShareManagementPage() {
  const { message } = AntdApp.useApp()
  const [createOpen, setCreateOpen] = useState(false)
  const [detailId, setDetailId] = useState<number>()
  const [revokeTarget, setRevokeTarget] = useState<ReceptionShareBatch>()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [status, setStatus] = useState<string>()
  const [keyword, setKeyword] = useState('')
  const [form] = Form.useForm<ReceptionShareCreatePayload>()
  const [revokeForm] = Form.useForm<{ reason: string }>()
  const sourceUserId = Form.useWatch('source_user_id', form)
  const targetUserId = Form.useWatch('target_user_id', form)

  const sourcesQuery = useReceptionShareCandidates('source')
  const targetsQuery = useReceptionShareCandidates('target')
  const groupsQuery = useReceptionShareableGroups(sourceUserId)
  const batchesQuery = useReceptionShareBatches({
    status,
    keyword: keyword.trim() || undefined,
    page,
    page_size: pageSize,
  })
  const detailQuery = useReceptionShareBatch(detailId)
  const createMutation = useCreateReceptionShare()
  const revokeMutation = useRevokeReceptionShare()

  const sourceOptions = (sourcesQuery.data?.items || []).map((user) => ({
    value: user.id,
    label: userLabel(user),
  }))
  const targetOptions = (targetsQuery.data?.items || [])
    .filter((user) => user.id !== sourceUserId)
    .map((user) => ({ value: user.id, label: userLabel(user) }))
  const groupOptions = (groupsQuery.data?.items || []).map((group) => ({
    value: group.room_id,
    label: `${group.room_name} · ${group.customer_name || '未配置客户'}`,
  }))

  const submitCreate = async () => {
    try {
      const values = await form.validateFields()
      const result = await createMutation.mutateAsync({
        ...values,
        reason: values.reason?.trim() || undefined,
      })
      const skipped = result.skipped.length
      message.success(`已共享 ${result.shared_count} 个群${skipped ? `，跳过 ${skipped} 个已有权限的群` : ''}`)
      form.resetFields()
      setCreateOpen(false)
      setDetailId(result.batch.id)
    } catch (error) {
      if (error instanceof Error && !('errorFields' in error)) message.error(error.message)
    }
  }

  const submitRevoke = async () => {
    if (!revokeTarget) return
    try {
      const { reason } = await revokeForm.validateFields()
      await revokeMutation.mutateAsync({ batchId: revokeTarget.id, reason: reason.trim() })
      message.success('该批次的剩余代管权限已全部收回')
      revokeForm.resetFields()
      setRevokeTarget(undefined)
    } catch (error) {
      if (error instanceof Error && !('errorFields' in error)) message.error(error.message)
    }
  }

  return (
    <div data-testid="reception-share-page" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <Title level={3} style={{ marginBottom: 4 }}>接待临时代管</Title>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          管理员可将一名接待员负责的多个群临时共享给另一名接待员。共享不改变原负责人，原接待员权限保持不变。
        </Paragraph>
      </div>
      <Alert
        showIcon
        type="info"
        title="代管人可处理群消息、工单草稿、工单与 SLA；不能修改群映射，也不能再次转交。"
      />
      <Space wrap>
        <Input.Search
          allowClear
          placeholder="批次号、接待账号或姓名"
          style={{ width: 300 }}
          onSearch={(value) => { setKeyword(value); setPage(1) }}
        />
        <Select
          allowClear
          placeholder="全部状态"
          style={{ width: 160 }}
          options={Object.entries(STATUS_META).map(([value, meta]) => ({ value, label: meta.label }))}
          onChange={(value) => { setStatus(value); setPage(1) }}
        />
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
          新建代管共享
        </Button>
      </Space>

      <Table
        rowKey="id"
        size="small"
        loading={batchesQuery.isLoading}
        dataSource={batchesQuery.data?.items || []}
        pagination={{
          current: page,
          pageSize,
          total: batchesQuery.data?.total || 0,
          showSizeChanger: true,
          onChange: (nextPage, nextPageSize) => { setPage(nextPage); setPageSize(nextPageSize) },
        }}
        columns={[
          { title: '批次号', dataIndex: 'batch_no', width: 210 },
          { title: '原接待员', render: (_, row) => userLabel(row.source_user) },
          { title: '代管人', render: (_, row) => userLabel(row.target_user) },
          { title: '群数', width: 100, render: (_, row) => `${row.active_count}/${row.total_count}` },
          { title: '状态', width: 120, render: (_, row) => statusTag(row.status) },
          { title: '创建时间', width: 160, render: (_, row) => formatMessageTime(row.created_at) },
          {
            title: '操作',
            width: 180,
            render: (_, row) => (
              <Space>
                <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => setDetailId(row.id)}>详情</Button>
                {row.active_count > 0 && (
                  <Button danger type="link" size="small" icon={<StopOutlined />} onClick={() => setRevokeTarget(row)}>
                    全部收回
                  </Button>
                )}
              </Space>
            ),
          },
        ]}
      />

      <Drawer title="新建临时代管" open={createOpen} size="large" onClose={() => setCreateOpen(false)}>
        <Alert
          showIcon
          type="warning"
          title="提交后立即生效。请确认代管人与群范围；如需结束代管，请从批次列表执行全部收回。"
          style={{ marginBottom: 16 }}
        />
        <Form form={form} layout="vertical">
          <Form.Item name="source_user_id" label="原接待员" rules={[{ required: true, message: '请选择原接待员' }]}>
            <Select
              showSearch
              optionFilterProp="label"
              loading={sourcesQuery.isLoading}
              options={sourceOptions}
              onChange={() => form.setFieldsValue({ target_user_id: undefined, room_ids: [] })}
            />
          </Form.Item>
          <Form.Item name="target_user_id" label="代管人" rules={[{ required: true, message: '请选择代管人' }]}>
            <Select
              showSearch
              optionFilterProp="label"
              disabled={!sourceUserId}
              loading={targetsQuery.isLoading}
              options={targetOptions}
            />
          </Form.Item>
          <Form.Item
            name="room_ids"
            label="共享群"
            rules={[{ required: true, type: 'array', min: 1, message: '请至少选择一个群' }]}
            extra={sourceUserId ? `该接待员当前共有 ${groupsQuery.data?.total || 0} 个可共享群` : '请先选择原接待员'}
          >
            <Select
              mode="multiple"
              showSearch
              optionFilterProp="label"
              disabled={!sourceUserId || !targetUserId}
              loading={groupsQuery.isLoading}
              options={groupOptions}
              maxTagCount="responsive"
            />
          </Form.Item>
          <Form.Item name="reason" label="代管原因">
            <Input.TextArea maxLength={500} showCount rows={3} placeholder="例如：请假期间由同事临时代管" />
          </Form.Item>
          <Space>
            <Button type="primary" loading={createMutation.isPending} onClick={() => void submitCreate()}>确认共享</Button>
            <Button data-testid="reception-share-create-cancel" onClick={() => setCreateOpen(false)}>取消</Button>
          </Space>
        </Form>
      </Drawer>

      <Drawer title="代管批次详情" open={Boolean(detailId)} size="large" onClose={() => setDetailId(undefined)}>
        {detailQuery.data && <ReceptionShareBatchDetail batch={detailQuery.data} />}
      </Drawer>

      <Modal
        title="收回本批次代管权限"
        open={Boolean(revokeTarget)}
        okText={`确认收回（${revokeTarget?.active_count || 0}）`}
        okButtonProps={{ danger: true }}
        confirmLoading={revokeMutation.isPending}
        onOk={() => void submitRevoke()}
        onCancel={() => { setRevokeTarget(undefined); revokeForm.resetFields() }}
      >
        <Paragraph>
          将收回批次 <Text code>{revokeTarget?.batch_no}</Text> 中仍有效的 {revokeTarget?.active_count || 0} 个代管权限。
          原接待员及代管人在创建批次前已有的权限不受影响。
        </Paragraph>
        <Form form={revokeForm} layout="vertical">
          <Form.Item name="reason" label="收回原因" rules={[{ required: true, whitespace: true, message: '请填写收回原因' }]}>
            <Input.TextArea maxLength={500} showCount rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
